import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faClock, faCheckCircle, faTimesCircle } from '@/fontawesome';
import type { RecommendationStats } from '@/models';

type StatsType = 'core' | 'earnings' | 'swing' | 'longterm';

interface RecommendationStatsCardProps {
  stats: RecommendationStats | null;
  loading?: boolean;
  type: StatsType;
}

export default function RecommendationStatsCard({ stats, loading = false, type }: RecommendationStatsCardProps) {
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
          <div className="animate-pulse flex gap-8 w-full">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
          </div>
        </div>
      </div>
    );
  }

  if (stats === null) {
    return null;
  }

  const finishedTotal = stats.success + stats.failed;

  const calculateFinishedPercentage = (value: number): string => {
    if (finishedTotal === 0) return '0';
    return ((value / finishedTotal) * 100).toFixed(1);
  };

  // Labels basierend auf Typ
  const failedLabel = type === 'core' || type === 'earnings'
    ? 'Eingebucht-Beendet:'
    : 'Mit-Verlust-Beendet:';

  return (
    <div className="max-w-7xl mx-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
      <div className="flex flex-wrap items-center justify-start gap-6 md:gap-8">
        {/* Gesamt */}
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faChartBar} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm text-gray-600 dark:text-gray-400">Gesamt:</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">{stats.total}</span>
        </div>

        {/* Offen - ohne Prozent */}
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faClock} className="w-4 h-4 text-blue-500 dark:text-blue-400" />
          <span className="text-sm text-gray-600 dark:text-gray-400">Offen:</span>
          <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
            {stats.open}
          </span>
        </div>

        {/* Erfolgreich-Beendet */}
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-500 dark:text-green-400" />
          <span className="text-sm text-gray-600 dark:text-gray-400">Erfolgreich-Beendet:</span>
          <span className="text-sm font-semibold text-green-600 dark:text-green-400">
            {stats.success}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-500">
            ({calculateFinishedPercentage(stats.success)}%)
          </span>
        </div>

        {/* Verlust-Beendet (Label je nach Typ) */}
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faTimesCircle} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm text-gray-600 dark:text-gray-400">{failedLabel}</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {stats.failed}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-500">
            ({calculateFinishedPercentage(stats.failed)}%)
          </span>
        </div>
      </div>
    </div>
  );
}
